import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader';
import { useFaceAuth } from '../hooks/useFaceAuth';
import { invalidateFaceBadgeCache } from '../components/FaceBadge';
import type { AuthUser } from '../hooks/useAuth';

interface Props { user: AuthUser; }

/** ステータスに応じたメッセージアイコン */
function StatusIcon({ status, done }: { status: string; done: boolean }) {
  if (done) return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="11" fill="#22c55e" />
      <path d="M6 12.5l4 4 8-8" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  if (status === 'error') return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="11" fill="#ef4444" />
      <path d="M8 8l8 8M16 8l-8 8" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
  if (status === 'detecting') return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
      <circle cx="12" cy="12" r="10" stroke="#0ea5e9" strokeWidth="3" strokeDasharray="30 40" strokeLinecap="round" />
    </svg>
  );
  if (status === 'loading') return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
      <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.4)" strokeWidth="3" strokeDasharray="30 40" strokeLinecap="round" />
    </svg>
  );
  // ready
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="9" r="4" stroke="rgba(255,255,255,0.7)" strokeWidth="2" />
      <path d="M5 20c0-3.866 3.134-7 7-7s7 3.134 7 7" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export const FaceRegisterPage: React.FC<Props> = ({ user }) => {
  const nav = useNavigate();
  const { videoRef, status, message, guide, startCamera, stopCamera, registerFace } = useFaceAuth();
  const [done, setDone] = useState(false);
  const [autoNavSec, setAutoNavSec] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 登録成功後3秒でSettings に戻る
  useEffect(() => {
    if (!done) return;
    setAutoNavSec(3);
    let sec = 3;
    const iv = setInterval(() => {
      sec -= 1;
      setAutoNavSec(sec);
      if (sec <= 0) {
        clearInterval(iv);
        stopCamera();
        nav('/settings');
      }
    }, 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  const handleRegister = async () => {
    const ok = await registerFace(user.user_id, user.user_name, user.user_role, user.company_id);
    if (ok) { setDone(true); invalidateFaceBadgeCache(); }
  };

  const handleRetry = () => {
    setDone(false);
    startCamera();
  };

  // 位置OK（guide==='ok'）のときだけ登録可能。エラー時は再試行のため許可。
  const canRegister = !done && status !== 'detecting' && (guide === 'ok' || status === 'error');
  // 枠色: 完了=緑 / 検出中=青 / 位置OK=緑 / それ以外=状態色
  const borderColor = done ? '#22c55e'
    : status === 'detecting' ? '#0ea5e9'
    : guide === 'ok' ? '#22c55e'
    : guide === 'searching' || guide === 'idle' ? 'rgba(255,255,255,0.4)'
    : '#f59e0b'; // far/near/off_center = オレンジ（調整を促す）

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0,
      background: 'linear-gradient(160deg, #0a2342 0%, #0f3460 60%, #16213e 100%)',
      fontFamily: 'Inter, sans-serif',
    }}>
      {/* CSS animation */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse-green { 0%,100% { box-shadow: 0 0 20px 4px rgba(34,197,94,0.4); } 50% { box-shadow: 0 0 40px 12px rgba(34,197,94,0.8); } }
        @keyframes detecting-dash { 0% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: -200; } }
      `}</style>

      {/* 見出し（カメラ穴の下から始める） */}
      <AppHeader
        title="顔認証を登録"
        subtitle={`${user.user_name} さんの顔を登録します`}
        background="transparent" color="#fff" subColor="rgba(255,255,255,0.6)"
        style={{ borderBottom: 'none', boxShadow: 'none' }}
      />

      <div style={{
        flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}>

      {/* カメラ + 楕円ガイド */}
      <div style={{ position: 'relative', width: 260, height: 300, marginBottom: 24 }}>

        {/* カメラ映像（楕円クリップ） */}
        <div style={{
          width: 260, height: 300, borderRadius: '50%',
          overflow: 'hidden',
          border: `3px solid ${borderColor}`,
          boxShadow: done
            ? '0 0 30px 8px rgba(34,197,94,0.6)'
            : status === 'detecting'
            ? '0 0 20px 4px rgba(14,165,233,0.5)'
            : undefined,
          transition: 'border-color 0.4s, box-shadow 0.4s',
          animation: done ? 'pulse-green 1.5s ease-in-out infinite' : undefined,
        }}>
          <video
            ref={videoRef}
            autoPlay muted playsInline
            style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
          />

          {/* 検出中オーバーレイ */}
          {status === 'detecting' && !done && (
            <div style={{
              position: 'absolute', inset: 0,
              background: 'rgba(14,165,233,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }} />
          )}

          {/* 成功オーバーレイ */}
          {done && (
            <div style={{
              position: 'absolute', inset: 0,
              background: 'rgba(34,197,94,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                <circle cx="40" cy="40" r="38" fill="rgba(34,197,94,0.4)" stroke="#22c55e" strokeWidth="3" />
                <path d="M22 40l13 13 23-23" stroke="white" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}

          {/* ローディングオーバーレイ */}
          {status === 'loading' && (
            <div style={{
              position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
            }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3" />
                <path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="3" strokeLinecap="round" />
              </svg>
              <span style={{ color: 'white', fontSize: 12 }}>カメラ起動中...</span>
            </div>
          )}
        </div>

        {/* 顔の位置ガイド（楕円の内側に薄いガイド線） */}
        {(status === 'ready' || status === 'detecting') && !done && (
          <svg
            width="260" height="300"
            viewBox="0 0 260 300"
            style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
          >
            <ellipse
              cx="130" cy="150" rx="75" ry="95"
              fill="none"
              stroke={status === 'detecting' ? 'rgba(14,165,233,0.7)' : 'rgba(255,255,255,0.25)'}
              strokeWidth="2"
              strokeDasharray={status === 'detecting' ? '12 6' : '8 8'}
              style={{
                animation: status === 'detecting'
                  ? 'detecting-dash 2s linear infinite'
                  : undefined,
              }}
            />
            {/* 四隅のコーナーマーカー */}
            {[
              [130 - 75, 150 - 60, 0],   // 左上付近
              [130 + 75, 150 - 60, 90],  // 右上付近
              [130 - 75, 150 + 60, 270], // 左下付近
              [130 + 75, 150 + 60, 180], // 右下付近
            ].map(([x, y, _], i) => (
              <g key={i} transform={`translate(${x},${y})`}>
                <line x1="-12" y1="0" x2="12" y2="0"
                  stroke={status === 'detecting' ? '#0ea5e9' : 'rgba(255,255,255,0.6)'}
                  strokeWidth="2.5" strokeLinecap="round" />
                <line x1="0" y1="-12" x2="0" y2="12"
                  stroke={status === 'detecting' ? '#0ea5e9' : 'rgba(255,255,255,0.6)'}
                  strokeWidth="2.5" strokeLinecap="round" />
              </g>
            ))}
          </svg>
        )}
      </div>

      {/* ステータス表示 */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        marginBottom: 8, minHeight: 36,
      }}>
        <StatusIcon status={status} done={done} />
        <span style={{
          fontSize: done ? 18 : 14,
          fontWeight: done ? 700 : 500,
          color: done ? '#86efac'
            : status === 'error' ? '#fca5a5'
            : status === 'detecting' ? '#7dd3fc'
            : 'rgba(255,255,255,0.8)',
          transition: 'all 0.3s',
        }}>
          {done ? '登録完了！' : message || '準備中...'}
        </span>
      </div>

      {/* 自動ナビゲーションカウントダウン */}
      {done && autoNavSec > 0 && (
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 20 }}>
          {autoNavSec}秒後に設定画面に戻ります
        </div>
      )}

      {/* ボタン */}
      <div style={{ width: '100%', maxWidth: 300, marginTop: done ? 0 : 16 }}>
        {!done ? (
          <>
            <button
              onClick={handleRegister}
              disabled={!canRegister}
              style={{
                width: '100%', padding: '16px 0', borderRadius: 16,
                border: 'none',
                background: canRegister
                  ? 'linear-gradient(135deg, #0ea5e9 0%, #2ec4b6 100%)'
                  : 'rgba(255,255,255,0.15)',
                color: 'white', fontSize: 16, fontWeight: 700,
                cursor: canRegister ? 'pointer' : 'not-allowed',
                opacity: canRegister ? 1 : 0.6,
                transition: 'all 0.3s',
                boxShadow: canRegister ? '0 4px 20px rgba(14,165,233,0.4)' : 'none',
              }}
            >
              {status === 'detecting' ? '登録中...' : guide === 'ok' ? '登録する' : '顔を枠に合わせてください'}
            </button>
            {status === 'error' && (
              <button
                onClick={handleRetry}
                style={{
                  marginTop: 10, width: '100%', padding: '13px 0', borderRadius: 14,
                  border: '1.5px solid rgba(255,255,255,0.25)',
                  background: 'rgba(255,255,255,0.08)',
                  color: 'rgba(255,255,255,0.8)', fontSize: 14, fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                カメラを再起動
              </button>
            )}
          </>
        ) : (
          <button
            onClick={() => { stopCamera(); nav('/settings'); }}
            style={{
              width: '100%', padding: '16px 0', borderRadius: 16,
              border: 'none',
              background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
              color: 'white', fontSize: 16, fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(34,197,94,0.4)',
            }}
          >
            設定画面に戻る
          </button>
        )}
      </div>

      {/* キャンセル */}
      {!done && (
        <button
          onClick={() => { stopCamera(); nav('/settings'); }}
          style={{
            marginTop: 14, background: 'none', border: 'none',
            color: 'rgba(255,255,255,0.35)', fontSize: 14, cursor: 'pointer',
          }}
        >
          キャンセル
        </button>
      )}
      </div>
    </div>
  );
};

export default FaceRegisterPage;
